import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Chip,
  Button, IconButton, TextField, Stack, Paper,
  CircularProgress, Tooltip, useMediaQuery, useTheme, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { Add, Luggage, Delete, ScubaDiving } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import ScheduleDiveDialog from './ScheduleDiveDialog';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import { diveTripsService } from '../services/diveTripsService';
import { useGroups } from '../hooks/useGroups';
import { useClubs } from '../hooks/useClubs';
import type { DiveTrip } from '../types/DiveTrip';
import type { ScheduledDive } from '../types/ScheduledDive';

const OpenTo = {
  Nobody: 'Nobody',
  Group: 'Group',
  Club: 'Club',
} as const;
type OpenToValue = typeof OpenTo[keyof typeof OpenTo];

const todayString = () => format(new Date(), 'yyyy-MM-dd');
// Dates are stored as UTC midnight — parsing the date-only substring keeps the calendar
// day stable regardless of the viewer's timezone (mirrors DiveCalendar's parseDiveDate).
const parseStoredDate = (iso: string) => parseISO(iso.slice(0, 10));

// ── Create trip dialog ───────────────────────────────────────────────────────

const CreateTripDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreate: (fields: {
    name: string;
    destination: string;
    startDate: string;
    endDate: string;
    notes: string;
    openToGroupId?: string;
    openToClubId?: string;
  }) => void;
}> = ({ open, onClose, onCreate }) => {
  const currentUserId = authService.getUser()?.userId ?? null;
  const { groups } = useGroups(currentUserId);
  const { clubs } = useClubs(currentUserId);

  const [name, setName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState(todayString());
  const [endDate, setEndDate] = useState(todayString());
  const [notes, setNotes] = useState('');
  const [openTo, setOpenTo] = useState<OpenToValue>(OpenTo.Nobody);
  const [openToGroupId, setOpenToGroupId] = useState('');
  const [openToClubId, setOpenToClubId] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setDestination('');
      setStartDate(todayString());
      setEndDate(todayString());
      setNotes('');
      setOpenTo(OpenTo.Nobody);
      setOpenToGroupId('');
      setOpenToClubId('');
    }
  }, [open]);

  const handleSubmit = () => {
    onCreate({
      name,
      destination,
      startDate,
      endDate,
      notes,
      openToGroupId: openTo === OpenTo.Group ? openToGroupId : undefined,
      openToClubId: openTo === OpenTo.Club ? openToClubId : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Dive Trip</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Trip Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Bahamas Adventure"
            inputProps={{ maxLength: 200 }}
            required
            fullWidth
          />
          <TextField
            label="Destination"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="e.g. Nassau, Bahamas"
            inputProps={{ maxLength: 200 }}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
          </Stack>
          {(groups.length > 0 || clubs.length > 0) && (
            <FormControl fullWidth size="small">
              <InputLabel>Open this trip to</InputLabel>
              <Select value={openTo} label="Open this trip to" onChange={(e) => setOpenTo(e.target.value as OpenToValue)}>
                <MenuItem value={OpenTo.Nobody}>Just my invited buddies</MenuItem>
                {groups.length > 0 && <MenuItem value={OpenTo.Group}>A specific dive group…</MenuItem>}
                {clubs.length > 0 && <MenuItem value={OpenTo.Club}>A specific club…</MenuItem>}
              </Select>
            </FormControl>
          )}
          {openTo === OpenTo.Group && (
            <FormControl fullWidth size="small">
              <InputLabel>Dive Group</InputLabel>
              <Select value={openToGroupId} label="Dive Group" onChange={(e) => setOpenToGroupId(e.target.value)}>
                {groups.map((g) => (
                  <MenuItem key={g.groupId} value={g.groupId}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {openTo === OpenTo.Club && (
            <FormControl fullWidth size="small">
              <InputLabel>Club</InputLabel>
              <Select value={openToClubId} label="Club" onChange={(e) => setOpenToClubId(e.target.value)}>
                {clubs.map((c) => (
                  <MenuItem key={c.clubId} value={c.clubId}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder="Trip theme, logistics, packing reminders…"
            inputProps={{ maxLength: 2000 }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || !startDate || !endDate || (openTo === OpenTo.Group && !openToGroupId) || (openTo === OpenTo.Club && !openToClubId)}
        >
          Create Trip
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Trips list (index) ───────────────────────────────────────────────────────

const DiveTripsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const [trips, setTrips] = useState<DiveTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const t = await diveTripsService.getMyDiveTrips();
      setTrips(t);
    } catch {
      showError('Failed to load dive trips');
    } finally {
      setLoading(false);
    }
  }, [userId, showError]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = useCallback(async (fields: {
    name: string; destination: string; startDate: string; endDate: string; notes: string;
    openToGroupId?: string; openToClubId?: string;
  }) => {
    try {
      const trip = await diveTripsService.createDiveTrip({
        name: fields.name.trim(),
        destination: fields.destination.trim() || undefined,
        startDate: `${fields.startDate}T00:00:00.000Z`,
        endDate: `${fields.endDate}T00:00:00.000Z`,
        notes: fields.notes.trim() || undefined,
        openToGroupId: fields.openToGroupId,
        openToClubId: fields.openToClubId,
      });
      showSuccess('Trip created!');
      setCreateOpen(false);
      navigate(`/trips/${trip.tripId}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create trip');
    }
  }, [showSuccess, showError, navigate]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="plan dive trips"
          description="Group multiple dives across several days into one named trip — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Dive Trips</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 40 }}>
            New Trip
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && trips.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Luggage sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No dive trips yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Plan a multi-day trip and group your dives under it.
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {trips.map(trip => (
            <Grid item xs={12} sm={6} md={isMobile ? 12 : 4} key={trip.tripId}>
              <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate(`/trips/${trip.tripId}`)}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{trip.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {format(parseStoredDate(trip.startDate), 'MMM d')} – {format(parseStoredDate(trip.endDate), 'MMM d, yyyy')}
                  </Typography>
                  {trip.destination && (
                    <Typography variant="body2" color="text.secondary">{trip.destination}</Typography>
                  )}
                  {(trip.openToGroupId || trip.openToClubId) && (
                    <Chip
                      label={trip.openToGroupId ? 'Open to a group' : 'Open to a club'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <CreateTripDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />
      </Box>
    </Box>
  );
};

// ── Trip detail ──────────────────────────────────────────────────────────────

const DiveTripDetail: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;

  const [trip, setTrip] = useState<DiveTrip | null>(null);
  const [dives, setDives] = useState<ScheduledDive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await diveTripsService.getDiveTrip(tripId);
      setTrip(detail.trip);
      setDives(detail.dives);
    } catch {
      setError('Failed to load trip');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { load(); }, [load]);

  const isOwner = trip?.ownerUserId === userId;

  const handleDelete = useCallback(async () => {
    if (!tripId) return;
    setDeleting(true);
    try {
      await diveTripsService.deleteDiveTrip(tripId);
      showSuccess('Trip deleted');
      navigate('/trips');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete trip');
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  }, [tripId, showSuccess, showError, navigate]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="plan dive trips"
          description="Sign in to view this trip."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  if (error || !trip) {
    return (
      <Box>
        <AppNavigation />
        <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>{error ?? 'Trip not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{trip.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {format(parseStoredDate(trip.startDate), 'MMM d')} – {format(parseStoredDate(trip.endDate), 'MMM d, yyyy')}
              {trip.destination ? ` · ${trip.destination}` : ''}
            </Typography>
            {(trip.openToGroupId || trip.openToClubId) && (
              <Chip label={trip.openToGroupId ? 'Open to a group' : 'Open to a club'} size="small" sx={{ mt: 1 }} />
            )}
          </Box>
          {isOwner && (
            <Tooltip title="Delete trip">
              <IconButton color="error" onClick={() => setDeleteConfirmOpen(true)}>
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {trip.notes && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{trip.notes}</Typography>
          </Paper>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Dives on this trip</Typography>
          {isOwner && (
            <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setScheduleDialogOpen(true)}>
              Add a Dive
            </Button>
          )}
        </Box>

        {dives.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ScubaDiving sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No dives added to this trip yet.</Typography>
          </Box>
        )}

        <Stack spacing={1.5}>
          {dives.map(dive => (
            <Paper
              key={dive.diveId}
              variant="outlined"
              sx={{ p: 2, cursor: 'pointer' }}
              onClick={() => navigate('/calendar')}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {dive.siteName || 'Dive'} · {format(parseStoredDate(dive.scheduledDate), 'MMM d, yyyy')}
                  </Typography>
                  {dive.charterCompany && (
                    <Typography variant="caption" color="text.secondary">{dive.charterCompany}</Typography>
                  )}
                </Box>
                <Chip label={`${dive.invitees.filter(i => i.status === 'accepted').length + 1} going`} size="small" />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>

      {tripId && (
        <ScheduleDiveDialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          onSaved={(dive) => {
            setDives(prev => [...prev, dive].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)));
          }}
          initialTripId={tripId}
        />
      )}

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete this trip?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The trip itself will be removed. Dives already scheduled under it stay on your calendar as standalone dives.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { DiveTripsList, DiveTripDetail };
